/**
 * useResourceGeolocations Hook
 * Merges live geolocation data with resource (driver/technician) data
 * for displaying on the Dispatch map
 */
import { useMemo, useCallback } from "react";
import { useGeolocation } from "../contexts/GeolocationContext";
import { useGetGeolocationsQuery } from "../redux/services/geolocations/geolocationApi";
import {
  isPlottableLatLng,
  toIdString,
} from "../utils/leg-route-location";

/**
 * @param {object} resource
 * @returns {string}
 */
const getResourceUserId = (resource) =>
  toIdString(
    resource?._id ||
      resource?.id ||
      resource?.userId ||
      resource?.user?._id ||
      resource?.user?.id
  );

/**
 * Merge resources with their live geolocation data
 * @param {Array} resources - Array of resource objects (drivers/technicians)
 * @returns {Object} - Object containing merged resources and helper methods
 */
export const useResourceGeolocations = (resources = []) => {
  const {
    geolocations: contextGeolocations,
    isLoading: contextIsLoading,
    isConnected,
    isSubscribed,
    getGeolocation,
    isLocationFresh,
    refreshGeolocations,
  } = useGeolocation();

  // Extract valid userIds from resources (stringify ObjectIds so the query runs)
  const userIds = useMemo(() => {
    if (!resources || resources.length === 0) return undefined;

    const ids = [
      ...new Set(resources.map(getResourceUserId).filter(Boolean)),
    ];

    return ids.length > 0 ? ids : undefined;
  }, [resources]);

  // Query geolocations for specific user IDs if available
  const {
    data: resourceGeolocations,
    isLoading: resourceIsLoading,
  } = useGetGeolocationsQuery(
    { userIds, freshnessMinutes: 30 },
    {
      skip: !userIds || userIds.length === 0,
    }
  );

  // Merge context geolocations with resource-specific geolocations
  const geolocations = useMemo(() => {
    const merged = { ...contextGeolocations };

    if (resourceGeolocations && Array.isArray(resourceGeolocations)) {
      resourceGeolocations.forEach((geo) => {
        const geoUserId = toIdString(geo.userId);
        if (geoUserId && geo.location) {
          merged[geoUserId] = {
            userId: geoUserId,
            fullName: geo.fullName,
            avatar: geo.avatar,
            location: geo.location,
            accuracy: geo.accuracy,
            heading: geo.heading,
            speed: geo.speed,
            timestamp: geo.timestamp,
            updatedAt: geo.updatedAt,
          };
        }
      });
    }

    return merged;
  }, [contextGeolocations, resourceGeolocations]);

  const isLoading = contextIsLoading || resourceIsLoading;

  /**
   * Resources with live location data merged in
   * Adds currentLocation property to each resource that has a fresh geolocation
   */
  const resourcesWithLocations = useMemo(() => {
    if (!resources || resources.length === 0) return [];

    return resources.map((resource) => {
      const userId = getResourceUserId(resource);

      if (!userId) return resource;

      const geo =
        geolocations[userId] ||
        getGeolocation(userId) ||
        getGeolocation(resource._id) ||
        getGeolocation(resource.id);

      const hasFreshLocation = geo && isLocationFresh(geo.updatedAt);

      if (hasFreshLocation && isPlottableLatLng(geo.location)) {
        return {
          ...resource,
          currentLocation: {
            lat: geo.location.lat,
            lng: geo.location.lng,
          },
          locationSource: "gps",
          geolocationMeta: {
            accuracy: geo.accuracy,
            heading: geo.heading,
            speed: geo.speed,
            timestamp: geo.timestamp,
            updatedAt: geo.updatedAt,
          },
        };
      }

      // On Shift without a fresh ping: plot the shift start location when it is real.
      if (isPlottableLatLng(resource.shiftLocation)) {
        return {
          ...resource,
          currentLocation: {
            lat: resource.shiftLocation.lat,
            lng: resource.shiftLocation.lng,
          },
          locationSource: "shift",
        };
      }

      return resource;
    });
  }, [resources, geolocations, getGeolocation, isLocationFresh]);

  /**
   * Get only resources that have fresh location data
   */
  const resourcesWithActiveLocations = useMemo(() => {
    return resourcesWithLocations.filter((resource) =>
      isPlottableLatLng(resource.currentLocation)
    );
  }, [resourcesWithLocations]);

  /**
   * Get geolocation for a specific resource by ID
   */
  const getResourceGeolocation = useCallback(
    (resourceId) => {
      const id = toIdString(resourceId);
      return geolocations[id] || getGeolocation(id) || getGeolocation(resourceId);
    },
    [geolocations, getGeolocation]
  );

  /**
   * Check if a specific resource has fresh location
   */
  const hasResourceLocation = useCallback(
    (resourceId) => {
      const geo = getResourceGeolocation(resourceId);
      return geo && isLocationFresh(geo.updatedAt);
    },
    [getResourceGeolocation, isLocationFresh]
  );

  /**
   * Get count of resources with active locations
   */
  const activeLocationCount = useMemo(() => {
    return resourcesWithActiveLocations.length;
  }, [resourcesWithActiveLocations]);

  return {
    // Merged data
    resourcesWithLocations,
    resourcesWithActiveLocations,
    activeLocationCount,

    // Helper methods
    getResourceGeolocation,
    hasResourceLocation,
    refreshGeolocations,

    // Connection status
    isLoading,
    isConnected,
    isSubscribed,
  };
};

export default useResourceGeolocations;
